import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  useTheme,
} from "@mui/material";
import { ControllerRenderProps, FieldPath, FieldValues } from "react-hook-form";
import { selectDefaultProps } from "../../../Forms/selectDefaultProps";
import { useFetchAvailableBidPacksForRateAgreement } from "../../RateAgreements/Hooks/useFetchAvailableBidPacksForRateAgreement";
import { RateAgreementBidPack } from "../../RateAgreements/RateAgreementBidPack";
import { bidPackObjectName } from "../BidPack";
import { emptyMenuItem } from "../../../Forms/SelectDefaultComponents";

interface BidPackSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  field: ControllerRenderProps<TFieldValues, TName>;
  errorMessage: string | undefined;
  onBidPackChanged?: (bidPackId: number, rateAgreementBidPacks: RateAgreementBidPack[]) => void;
  rateAgreementId: number | null;
}

export function BidPackSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  field,
  errorMessage,
  onBidPackChanged,
  rateAgreementId,
  disabled,
  ...otherProps
}: BidPackSelectProps<TFieldValues, TName> & SelectProps) {
  const { data: bidPacksForRateAgreements } = useFetchAvailableBidPacksForRateAgreement(
    rateAgreementId ?? 0
  );

  const theme = useTheme();

  return (
    <FormControl fullWidth size="small">
      <InputLabel id="bidPack-select" shrink>
        {bidPackObjectName}
      </InputLabel>
      <Select
        {...field}
        {...otherProps}
        {...selectDefaultProps}
        labelId="bidPack-label"
        label={bidPackObjectName}
        onChange={(event) => {
          if (onBidPackChanged) {
            onBidPackChanged(event.target.value as number, bidPacksForRateAgreements || []);
            field.onChange(event);
          }
        }}
        disabled={disabled}
      >
        {emptyMenuItem(0)}
        {bidPacksForRateAgreements &&
          bidPacksForRateAgreements.map((bidPack) => (
            <MenuItem key={bidPack.Id} value={bidPack.Id}>
              {bidPack.Name}
            </MenuItem>
          ))}
      </Select>
      <FormHelperText sx={{ color: theme.palette.error.main }}>{errorMessage}</FormHelperText>
    </FormControl>
  );
}
